import { defineStore } from 'pinia';
import { proposeMapping } from '@vft/core/mapping/proposer.js';
import { useAuditStore } from './auditStore.js';
import { useDefinitionStore } from './definitionStore.js';
import { useValueMapStore } from './valueMapStore.js';

export const SPECIAL = Object.freeze({
  IGNORE: 'ignore',
  AUTO_VALUE: 'auto_value',
  ONE_TO_MANY: 'one_to_many',
});

function getFieldId(field) {
  return field.id || field.name || field.label;
}

export const useMappingStore = defineStore('mapping', {
  state: () => ({
    mapping: {},
    autoValues: {},
    extraTargets: {},
    columnActions: {},
    proposals: [],
  }),
  getters: {
    fieldsById() {
      const def = useDefinitionStore();
      const map = new Map();
      for (const f of def.fields) {
        map.set(getFieldId(f), f);
      }
      return map;
    },
    mappedFieldCount: (s) => Object.values(s.mapping).filter(Boolean).length,
    // IDs de camps OBLIGATORIS que no tenen ni columna origen ni valor fix assignat.
    // La càrrega a Sherpa fallaria segur, així que bloquegen l'avanç.
    unfilledMandatory() {
      const def = useDefinitionStore();
      const mappedFieldIds = new Set(
        Object.entries(this.mapping).filter(([, col]) => col).map(([fid]) => fid),
      );
      const out = [];
      for (const f of def.fields) {
        const m = f.validation?.mandatory ?? f.mandatory;
        const isMand = m === true || m?.required === true || m?.value === true;
        if (!isMand) continue;
        const fid = getFieldId(f);
        if (mappedFieldIds.has(fid)) continue; // té columna origen
        const av = this.autoValues[fid];
        if (av == null || av === '') out.push(fid); // obligatori, sense origen i sense valor
      }
      return out;
    },
    // No es pot avançar mentre quedi algun obligatori sense origen i sense valor.
    requiredComplete() {
      return this.unfilledMandatory.length === 0;
    },
    confidenceCounts: (s) => {
      const out = { high: 0, medium: 0, low: 0, none: 0 };
      for (const p of s.proposals) out[p.confidence] = (out[p.confidence] || 0) + 1;
      return out;
    },
  },
  actions: {
    propose() {
      const audit = useAuditStore();
      const def = useDefinitionStore();
      if (!audit.hasResult || !def.hasDefinition) {
        this.proposals = [];
        return;
      }
      this.proposals = proposeMapping(audit.result.columns, def.fields);
      this.mapping = {};
      for (const p of this.proposals) {
        if (p.column) this.mapping[p.fieldId] = p.column;
      }
      useValueMapStore().reset();
    },
    swapMapping(fieldIdA, fieldIdB) {
      const next = { ...this.mapping };
      const a = next[fieldIdA] || null;
      const b = next[fieldIdB] || null;
      if (b) next[fieldIdA] = b; else delete next[fieldIdA];
      if (a) next[fieldIdB] = a; else delete next[fieldIdB];
      this.mapping = next;
    },
    setMapping(fieldId, columnName) {
      if (!columnName) {
        delete this.mapping[fieldId];
      } else {
        this.mapping[fieldId] = columnName;
      }
      this.mapping = { ...this.mapping };
    },
    addExtraTarget(fieldId, sourceColumn) {
      const list = new Set(this.extraTargets[fieldId] || []);
      list.add(sourceColumn);
      this.extraTargets = { ...this.extraTargets, [fieldId]: [...list] };
    },
    setAutoValue(fieldId, value) {
      this.autoValues = { ...this.autoValues, [fieldId]: value };
    },
    setColumnAction(columnName, action) {
      this.columnActions = { ...this.columnActions, [columnName]: action };
    },
    reset() {
      this.mapping = {};
      this.autoValues = {};
      this.extraTargets = {};
      this.columnActions = {};
      this.proposals = [];
    },
  },
});
